import React, { useState, lazy, Suspense } from "react";
import axios from "axios";
import { Mail, Phone, MessageCircle, MapPin, Clock, ExternalLink } from "lucide-react";
import SEO, { localBusinessLd } from "../components/SEO";
import TermsModal from "../components/hidroverse/TermsModal";

// Map only mounts when the user opens it — keeps initial page light.
const LocationPicker = lazy(() => import("../components/hidroverse/LocationPicker"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Water purification (commercial)",
  "Wastewater treatment",
  "Pumps & pressure systems",
  "Source & storage (boreholes, rainwater)",
  "Pool & spa systems",
  "Irrigation systems",
  "Telemetry & monitoring",
  "Rentals & as-a-service",
  "Service / SLA / compliance",
  "Residential / retail (Nelspruit store)",
  "Something else",
];

const CONTACT_METHODS = [
  { v: "Phone", icon: Phone },
  { v: "Email", icon: Mail },
  { v: "WhatsApp", icon: MessageCircle },
];

export default function Contact() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    address: "",
    location_lat: null,
    location_lng: null,
    interest: "",
    preferred_contact: "Email",
    message: "",
    accepted_terms: false,
  });
  const [showMap, setShowMap] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.first_name.trim() &&
    form.last_name.trim() &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    form.mobile.trim().length >= 4 &&
    form.interest &&
    form.preferred_contact &&
    form.message.trim().length >= 10 &&
    form.accepted_terms;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail?.toString() || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="min-h-[88vh] flex items-center justify-center px-6 pt-32 pb-16"
        style={{ background: "#FAFAF7" }}
        data-testid="contact-success"
      >
        <div className="max-w-xl text-center">
          <div
            className="mb-5 text-[0.84rem] font-semibold"
            style={{ color: "rgba(12,42,64,0.55)" }}
          >
            Message received
          </div>
          <h1
            className="font-display mb-6"
            style={{
              fontSize: "clamp(2.4rem, 5vw, 4.5rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              fontWeight: 500,
              color: "#0C2A40",
            }}
          >
            Thanks — we have your enquiry.
          </h1>
          <p
            className="font-light"
            style={{ fontSize: "1.05rem", color: "rgba(26,31,37,0.75)", lineHeight: 1.7 }}
          >
            Our team will assess your submission and get in contact with you through the
            channel you chose — typically within one business day. If it's urgent, call us on{" "}
            <a href="tel:+27723207326" style={{ color: "#1FB6C1", fontWeight: 600 }}>+27 72 320 7326</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" style={{ background: "#FAFAF7" }}>
      <SEO
        title="Contact"
        description="Get in touch with HidroVerse. Name, email, mobile, the basics — we respond within one business day. Office in Nelspruit, Mpumalanga, South Africa."
        path="/contact"
        ldJson={localBusinessLd()}
      />

      {/* HERO */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-36 pb-12 md:pb-16"
        style={{ background: "#070A0D", color: "#F5F7FA" }}
      >
        <div className="max-w-[1300px] mx-auto">
          <div
            className="font-mono uppercase mb-5"
            style={{ fontSize: "0.62rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
          >
            Contact us
          </div>
          <h1
            className="font-display"
            style={{
              fontSize: "clamp(2.4rem, 6vw, 5rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.025em",
              fontWeight: 300,
              maxWidth: "22ch",
            }}
          >
            Share your water needs<br />
            <span style={{ color: "#1FB6C1", fontWeight: 600 }}>with our team.</span>
          </h1>
          <p
            className="mt-7 max-w-2xl font-light"
            style={{ fontSize: "1.02rem", color: "#9AA7B4", lineHeight: 1.65 }}
          >
            The basics — name, mobile, email, what you're after. Our team will assess your
            submission and get in contact with you through the channel you prefer.
          </p>
        </div>
      </section>

      {/* TWO-COLUMN BODY: left = direct contact, right = form */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="max-w-[1300px] mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT — direct contact + value props */}
          <aside className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <div className="space-y-7">
              <div>
                <div
                  className="font-mono uppercase mb-2"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
                >
                  <Mail size={11} className="inline mr-2 -mt-0.5" strokeWidth={2} />Email
                </div>
                <a
                  href="mailto:nelspruit@hidroverse.co.za"
                  className="font-display block transition-colors hover:opacity-80 break-all"
                  style={{ fontSize: "1.05rem", color: "#0C2A40", fontWeight: 500 }}
                  data-testid="contact-email"
                >
                  nelspruit@hidroverse.co.za
                </a>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <a
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=nelspruit@hidroverse.co.za&su=Enquiry%20via%20HidroVerse.co.za"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 transition-colors hover:opacity-80"
                    style={{ fontSize: "0.78rem", color: "#1FB6C1", fontWeight: 600 }}
                    data-testid="contact-email-gmail"
                  >
                    Open in Gmail
                    <ExternalLink size={11} strokeWidth={2} />
                  </a>
                  <span style={{ color: "rgba(12,42,64,0.25)" }}>·</span>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText("nelspruit@hidroverse.co.za");
                        setEmailCopied(true);
                        setTimeout(() => setEmailCopied(false), 1800);
                      } catch (e) {
                        /* ignore */
                      }
                    }}
                    className="inline-flex items-center gap-1.5 transition-colors hover:opacity-80"
                    style={{
                      fontSize: "0.78rem",
                      color: emailCopied ? "#0C2A40" : "#1FB6C1",
                      fontWeight: 600,
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                    data-testid="contact-email-copy"
                  >
                    {emailCopied ? "Copied" : "Copy address"}
                  </button>
                </div>
              </div>

              <div>
                <div
                  className="font-mono uppercase mb-2"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
                >
                  <Phone size={11} className="inline mr-2 -mt-0.5" strokeWidth={2} />Call
                </div>
                <a
                  href="tel:+27723207326"
                  className="font-display block transition-colors hover:opacity-80"
                  style={{ fontSize: "1.05rem", color: "#0C2A40", fontWeight: 500 }}
                  data-testid="contact-phone"
                >
                  +27 72 320 7326
                </a>
                <a
                  href="https://wa.me/27723207326?text=Hi%20HidroVerse%20%E2%80%94%20I%27d%20like%20to%20enquire%20about%20a%20water%20system."
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 mt-2 transition-colors hover:opacity-80"
                  style={{ fontSize: "0.88rem", color: "#1FB6C1", fontWeight: 600 }}
                  data-testid="contact-whatsapp"
                >
                  <MessageCircle size={14} strokeWidth={2} /> WhatsApp us
                  <ExternalLink size={11} strokeWidth={2} />
                </a>
              </div>

              <div>
                <div
                  className="font-mono uppercase mb-2"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
                >
                  <MapPin size={11} className="inline mr-2 -mt-0.5" strokeWidth={2} />Visit
                </div>
                <a
                  href="https://share.google/Tfujb07pMTGCrLnwJ"
                  target="_blank"
                  rel="noreferrer"
                  className="font-light block transition-colors hover:opacity-80"
                  style={{ fontSize: "0.96rem", color: "#0C2A40", lineHeight: 1.55 }}
                  data-testid="contact-address"
                >
                  HidroVerse Nelspruit (Pty) Ltd<br />
                  Waterfall Park, Unit C1<br />
                  Waterfall Avenue, Nelspruit, 1200
                  <span
                    className="inline-flex items-center gap-1 ml-1 align-baseline"
                    style={{ color: "#1FB6C1", fontWeight: 600, fontSize: "0.78rem" }}
                  >
                    <ExternalLink size={11} strokeWidth={2} />
                  </span>
                </a>
                <div
                  className="mt-1 font-mono uppercase"
                  style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.5)", fontWeight: 600 }}
                >
                  Open in Google Maps
                </div>
              </div>

              <div>
                <div
                  className="font-mono uppercase mb-2"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
                >
                  <Clock size={11} className="inline mr-2 -mt-0.5" strokeWidth={2} />Hours
                </div>
                <div
                  className="font-light"
                  style={{ fontSize: "0.96rem", color: "rgba(26,31,37,0.75)", lineHeight: 1.55 }}
                >
                  Mon–Fri · 08:00–17:00<br />
                  <span style={{ color: "#0C2A40", fontWeight: 600 }}>Emergency response 24/7</span>
                </div>
              </div>

              <div
                className="p-5"
                style={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(12,42,64,0.08)",
                  borderLeft: "2px solid #1FB6C1",
                  borderRadius: 6,
                }}
              >
                <div
                  className="font-mono uppercase mb-2"
                  style={{ fontSize: "0.58rem", letterSpacing: "0.26em", color: "#1FB6C1", fontWeight: 700 }}
                >
                  What happens next
                </div>
                <p
                  className="font-light"
                  style={{ fontSize: "0.9rem", color: "rgba(26,31,37,0.78)", lineHeight: 1.55 }}
                >
                  Our team will assess your submission and get in contact with you through the
                  channel you prefer — typically within one business day.
                </p>
              </div>
            </div>
          </aside>

          {/* RIGHT — form */}
          <form
            onSubmit={submit}
            noValidate
            className="lg:col-span-8 p-7 md:p-10"
            style={{
              background: "#FFFFFF",
              border: "1px solid rgba(12,42,64,0.08)",
              borderRadius: 12,
              boxShadow: "0 1px 2px rgba(12,42,64,0.04), 0 14px 36px -16px rgba(12,42,64,0.10)",
            }}
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
              <Field id="first_name" label="First name" required>
                <input
                  id="first_name"
                  type="text"
                  autoComplete="given-name"
                  value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                  required
                  data-testid="field-first-name"
                  style={inputStyle}
                />
              </Field>
              <Field id="last_name" label="Surname" required>
                <input
                  id="last_name"
                  type="text"
                  autoComplete="family-name"
                  value={form.last_name}
                  onChange={(e) => update("last_name", e.target.value)}
                  required
                  data-testid="field-last-name"
                  style={inputStyle}
                />
              </Field>
              <Field id="email" label="Email" required>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  required
                  data-testid="field-email"
                  style={inputStyle}
                />
              </Field>
              <Field id="mobile" label="Mobile" required>
                <input
                  id="mobile"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+27 …"
                  value={form.mobile}
                  onChange={(e) => update("mobile", e.target.value)}
                  required
                  data-testid="field-mobile"
                  style={inputStyle}
                />
              </Field>
            </div>

            {/* Site address (optional + map pin) */}
            <div className="mt-6">
              <Field id="address" label="Site address" hint="(optional — drop a pin below if your site is rural or off the grid)">
                <input
                  id="address"
                  type="text"
                  autoComplete="street-address"
                  placeholder="Street address, town or area"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  data-testid="field-address"
                  style={inputStyle}
                />
              </Field>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowMap((v) => !v)}
                  className="inline-flex items-center gap-2 font-mono uppercase transition-colors hover:opacity-80"
                  style={{
                    fontSize: "0.62rem",
                    letterSpacing: "0.22em",
                    color: "#1FB6C1",
                    fontWeight: 700,
                  }}
                  data-testid="toggle-map"
                >
                  <MapPin size={12} strokeWidth={2} />
                  {showMap ? "Hide map" : form.location_lat ? "Edit pin on map" : "Drop a pin on the map"}
                </button>
              </div>
              {showMap && (
                <div className="mt-4">
                  <Suspense
                    fallback={
                      <div
                        className="rounded-lg flex items-center justify-center font-light"
                        style={{ height: 280, background: "#f0f2f2", color: "rgba(12,42,64,0.6)", fontSize: "0.9rem" }}
                      >
                        Loading map…
                      </div>
                    }
                  >
                    <LocationPicker
                      value={{ lat: form.location_lat, lng: form.location_lng }}
                      onChange={({ lat, lng }) => {
                        update("location_lat", lat);
                        update("location_lng", lng);
                      }}
                    />
                  </Suspense>
                </div>
              )}
            </div>

            {/* Interest + preferred contact */}
            <div className="mt-6 grid sm:grid-cols-2 gap-5 md:gap-6">
              <Field id="interest" label="What are you interested in?" required>
                <select
                  id="interest"
                  value={form.interest}
                  onChange={(e) => update("interest", e.target.value)}
                  required
                  data-testid="field-interest"
                  style={inputStyle}
                >
                  <option value="">Select…</option>
                  {INTERESTS.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </Field>
              <Field id="preferred_contact" label="Preferred contact method" required>
                <div className="flex flex-wrap gap-2 pt-1.5" role="radiogroup" aria-label="Preferred contact method">
                  {CONTACT_METHODS.map(({ v, icon: Icon }) => {
                    const selected = form.preferred_contact === v;
                    return (
                      <button
                        type="button"
                        key={v}
                        role="radio"
                        aria-checked={selected}
                        onClick={() => update("preferred_contact", v)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full transition-colors duration-200"
                        style={{
                          border: `1px solid ${selected ? "#1FB6C1" : "rgba(12,42,64,0.16)"}`,
                          background: selected ? "rgba(31,182,193,0.10)" : "#FFFFFF",
                          color: "#0C2A40",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                        }}
                        data-testid={`contact-method-${v.toLowerCase()}`}
                      >
                        <Icon size={14} strokeWidth={2} />
                        {v}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>

            {/* Message */}
            <div className="mt-6">
              <Field
                id="message"
                label="How can we help?"
                hint="Give us as much detail as possible — current setup, problems, demand, timelines — so the right person can call you."
                required
              >
                <textarea
                  id="message"
                  rows={6}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  required
                  data-testid="field-message"
                  style={{ ...inputStyle, resize: "vertical", minHeight: 140 }}
                />
              </Field>
            </div>

            {/* Terms checkbox */}
            <label
              className="mt-6 flex items-start gap-3 cursor-pointer select-none"
              data-testid="terms-checkbox-label"
            >
              <input
                type="checkbox"
                checked={form.accepted_terms}
                onChange={(e) => update("accepted_terms", e.target.checked)}
                className="mt-1 shrink-0 cursor-pointer"
                style={{ width: 18, height: 18, accentColor: "#1FB6C1" }}
                data-testid="terms-checkbox"
                required
              />
              <span
                className="font-light"
                style={{ fontSize: "0.88rem", color: "rgba(26,31,37,0.85)", lineHeight: 1.5 }}
              >
                I agree to the HidroVerse{" "}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setTermsOpen(true);
                  }}
                  className="underline transition-colors hover:opacity-80"
                  style={{ color: "#1FB6C1", fontWeight: 600 }}
                  data-testid="open-terms"
                >
                  Terms &amp; Conditions
                </button>{" "}
                and POPIA-compliant processing of my contact details for the purpose of this enquiry.
              </span>
            </label>

            {/* Error */}
            {error && (
              <div
                className="mt-5 p-3 rounded font-light"
                style={{
                  background: "rgba(220,38,38,0.06)",
                  border: "1px solid rgba(220,38,38,0.25)",
                  color: "#9b1c1c",
                  fontSize: "0.88rem",
                }}
                data-testid="contact-error"
              >
                {error}
              </div>
            )}

            {/* Submit */}
            <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
              <div
                className="font-light"
                style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.55)" }}
              >
                We typically reply within one business day.
              </div>
              <button
                type="submit"
                disabled={!canSubmit || loading}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02]"
                style={{
                  background: "#1FB6C1",
                  color: "#070A0D",
                  fontSize: "0.78rem",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
                data-testid="contact-submit"
              >
                {loading ? "Sending…" : "Send message"} <span aria-hidden>→</span>
              </button>
            </div>
          </form>
        </div>
      </section>

      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}

// --- Field shell + shared input styling ---
const inputStyle = {
  width: "100%",
  padding: "0.75rem 0.9rem",
  border: "1px solid rgba(12,42,64,0.12)",
  borderRadius: 6,
  background: "#FFFFFF",
  color: "#0C2A40",
  fontSize: "0.95rem",
  fontFamily: "inherit",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
};

function Field({ id, label, hint, required, children }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-mono uppercase mb-2"
        style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: "#0C2A40", fontWeight: 700 }}
      >
        {label} {required && <span style={{ color: "#1FB6C1" }}>*</span>}
      </label>
      {children}
      {hint && (
        <div
          className="mt-1.5 font-light"
          style={{ fontSize: "0.76rem", color: "rgba(12,42,64,0.55)", lineHeight: 1.4 }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
